import { faCircle, faSearch, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from './ErrorBoundary';
import { NoDataComponent } from './NoDataComponent';
import { BranchesModal } from './Dashboard/BranchesModal';
import { BranchListItem } from '../models/dashboard-models';
import { BranchesContext } from '../context/BranchesContext';
import { ErrorComponent } from './ErrorComponent';
import { RequestProvider } from '../context/RequestContext';
import MaterialReactTable, { MRT_ColumnDef } from 'material-react-table';
import { NewRequest } from '../pages/NewRequest';
import { NewRequestContext } from '../context/NewRequestContext';
import {
    useGetBranchByIdInRequestView as fetchBranchByIdInRequestView,
    useGetClientByCompanyName as fetchClientByCompanyName,
    useGetClients,
} from '../utils/clients-hooks';
import { useAsyncResource } from 'use-async-resource';
import { useGetUsers } from '../utils/users-hooks';

export const Branches = ({
                             activeTab = 'branches',
                             branches,
                             selectable,
                              onLoadMore,
                              hasMore,
                              loadingMore,
                         }: {
    activeTab?: string;
    branches: BranchListItem[];
    selectable?: boolean;
    onLoadMore?: () => Promise<void>;
    hasMore?: boolean;
    loadingMore?: boolean;
}) => {
    const { t } = useTranslation('translation');

    const [getAllUsers] = useAsyncResource(useGetUsers, {}, 'ADMIN,DISTRIBUTOR,INSTALLER,COORDINATOR');
    const { items: users } = getAllUsers();
    const [showNewRequestModal, setShowNewRequestModal] = useState(false);
    const { onBranchSelect, onClientSelect } = useContext(NewRequestContext);

    const {
        selectBranch,
        taskManagerBranches,
        setTaskManagerBranches,
        selectedBranch,
        selectedBranches,
        loadMoreDashboardBranches,
        hasMoreDashboardBranches,
        dashboardBranchesLoading,
        filterBranchesBySearch,
    } = useContext(BranchesContext);

    const FetchData = async (branchToOpen: BranchListItem) => {
        const companyName = branchToOpen.companyName ?? '';
        const branchId = branchToOpen.id;

        if (!companyName || !branchId) {
            return;
        }

        const client = await fetchClientByCompanyName(companyName);
        const branch = await fetchBranchByIdInRequestView(branchId);
        const distributor = users.find((user) => user?.id === branchToOpen.assignedDistributor);
        const nextMaintenanceDate = branch.nextMaintenanceDate ?? branchToOpen.nextMaintenanceDate ?? '';
        onClientSelect(client);
        onBranchSelect({
            ...branch,
            nextMaintenanceDate,
            assignedDistributor: distributor
        });
        setShowNewRequestModal(true);
        localStorage.setItem('isModalOpen', JSON.stringify(true));
        localStorage.setItem('selectedClient', JSON.stringify(client));
    };

    const usesExternalPagination = Boolean(onLoadMore);
    const loadMore = useCallback(async () => {
        if (selectable) {
            await loadMoreDashboardBranches();
            return;
        }

        if (onLoadMore) {
            await onLoadMore();
            return;
        }

        const itemsSliceStart = taskManagerBranches.length;
        const itemsSliceEnd = taskManagerBranches.length + 20;
        setTaskManagerBranches([...taskManagerBranches, ...branches.slice(itemsSliceStart, itemsSliceEnd)]);
    }, [
        branches,
        loadMoreDashboardBranches,
        onLoadMore,
        selectable,
        setTaskManagerBranches,
        taskManagerBranches,
    ]);

    const [search, setSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasActiveSearch, setHasActiveSearch] = useState(false);

    useEffect(() => {
        if (!selectable) {
            setTaskManagerBranches(usesExternalPagination ? branches : branches.slice(0, 30));
        }
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [branches, selectable, setTaskManagerBranches, usesExternalPagination]);

    useEffect(() => {
        if (activeTab === 'branches') {
            setSearch('');
        }
    }, [activeTab]);

    const columns = useMemo<MRT_ColumnDef<BranchListItem>[]>(
        () => [
            {
                id: 'assignedTask',
                accessorFn: (row) => {
                    return row.assignedTask ? (
                        <FontAwesomeIcon
                            className=' fa-fw mx-1'
                            icon={faUserCircle}
                            color={row.state}
                        ></FontAwesomeIcon>
                    ) : (
                        <FontAwesomeIcon icon={faCircle} color={row.state} className='mx-1'></FontAwesomeIcon>
                    );
                },
                header: '',
                size: 30,
                enableResizing: false,
            },
            {
                accessorKey: 'name',
                header: t('table.branchName'),
                size: 120,
            },
            {
                accessorKey: 'companyName',
                header: t('table.companyName'),
                size: 150,
            },
            {
                accessorKey: 'branchAddress',
                header: t('table.companyAddress'),
                size: 190,
            },
            {
                accessorKey: 'phone',
                header: t('table.phone'),
                size: 150,
            },
            {
                accessorKey: 'maintenanceDays',
                header: t('table.days'),
                size: 100,
            },
            {
                accessorFn: (row) => {
                    const distributor = users.find((user) => user.id === row.assignedDistributor);
                    return distributor ? distributor.name : '';
                },
                accessorKey: 'assignedDistributor',
                // accessorKey: 'distributorName',
                header: t('table.assignedDistributor'),
                size: 100,
            },
        ],
        [t, users],
    );

    const searchInputRef = useRef<HTMLInputElement>(null);
    const parentContainerRef = useRef<HTMLDivElement | null>(null);
    const searchContainerRef = useRef<HTMLDivElement | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);
    const [tableMaxHeight, setTableMaxHeight] = useState<number>();

    useEffect(() => {
        const parentContainer = parentContainerRef.current;
        const searchContainer = searchContainerRef.current;

        if (!parentContainer || !searchContainer) {
            return;
        }

        const updateTableHeight = () => {
            const nextHeight = Math.max(parentContainer.clientHeight - searchContainer.clientHeight, 0);
            setTableMaxHeight((currentHeight) => currentHeight === nextHeight ? currentHeight : nextHeight);
        };

        updateTableHeight();

        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(updateTableHeight);
        });

        resizeObserver.observe(parentContainer);
        resizeObserver.observe(searchContainer);
        window.addEventListener('resize', updateTableHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateTableHeight);
        };
    }, []);

    const handleSearch = () => {
        const normalizedSearch = search.trim();

        if (selectable) {
            setHasActiveSearch(false);
            filterBranchesBySearch(normalizedSearch);
            return;
        }

        setHasActiveSearch(normalizedSearch.length > 0);
        if (!normalizedSearch) {
            setTaskManagerBranches(usesExternalPagination ? branches : branches.slice(0, 30));
            return;
        }

        setIsSearching(true);
        // setBranchesFilter({ ...branchesFilter, search });
        // setIsSearching(false);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useGetClients({ filter: { search, tags: [] } }).then((clients) => {
            const filteredList = branches.filter((item) => {
                const distributorName = users.find((user) => user.id === item.assignedDistributor)?.name ?? '';
                const client = clients?.items.find((client) => client.companyName === item.companyName);
                const allFields = item.name
                    .concat(
                        item.companyName ?? '',
                        item.branchAddress ?? '',
                        item.city ?? '',
                        item.phone ?? '',
                        distributorName,
                    )
                    .toLowerCase();
                return allFields.includes(normalizedSearch.toLowerCase()) || (client && client.companyName === item.companyName);
            });
            setTaskManagerBranches(filteredList.slice(0, 30));
            setIsSearching(false);
        }).catch(() => {
            setIsSearching(false);
        });
    };
    const isBranchesLoading = isSearching || dashboardBranchesLoading || Boolean(loadingMore);
    const canLoadMore = selectable
        ? hasMoreDashboardBranches
        : onLoadMore
            ? Boolean(hasMore) && !hasActiveSearch
            : taskManagerBranches.length < branches.length && !hasActiveSearch;
    const displayedBranches = taskManagerBranches;

    const handleTableScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 24;
        if (isAtBottom && canLoadMore) {
            loadMore().then();
        }
    }, [canLoadMore, loadMore]);

    useEffect(() => {
        if (!canLoadMore || isBranchesLoading || !tableMaxHeight) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            const tableContainer = tableContainerRef.current;
            if (!tableContainer) {
                return;
            }

            const hasOverflow = tableContainer.scrollHeight > tableContainer.clientHeight + 1;
            if (!hasOverflow) {
                loadMore().then();
            }
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [canLoadMore, displayedBranches.length, isBranchesLoading, loadMore, tableMaxHeight]);

    return (
        <>
            <div
                ref={parentContainerRef}
                className='w-100 h-100 position-relative branches-container d-flex flex-column min-h-0'
                id='scrollableDiv'
            >
                <Form.Group
                    ref={searchContainerRef}
                    id='searchContainer'
                    className=' flex-center search w-100 align-items-start p-2'
                    controlId='searchForm'
                >
                    <Form.Control
                        ref={searchInputRef}
                        autoFocus={true}
                        type='search'
                        value={search}
                        className='form-control shadow-none '
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSearch();
                            }
                        }}
                    ></Form.Control>

                    <Button type='button' className='btn btn-primary' onClick={handleSearch}>
                        <FontAwesomeIcon icon={faSearch} className='fa-fw'/>
                    </Button>
                </Form.Group>
                <ErrorBoundary fallback={<ErrorComponent errorText={t('branches')}/>}>
                    <Suspense fallback={<Spinner animation='border'/>}>
                        {displayedBranches.length > 0 ? (
                            <div className='position-relative d-flex flex-column flex-grow-1 min-h-0'>
                                {isBranchesLoading && (
                                    <div
                                        className='position-absolute d-flex align-items-center justify-content-center w-100 h-100'
                                        style={{ zIndex: 2, pointerEvents: 'none' }}
                                    >
                                        <Spinner animation='border' />
                                    </div>
                                )}
                                <MaterialReactTable
                                    columns={columns}
                                    data={displayedBranches}
                                    enableColumnActions={false}
                                    enableColumnFilters={false}
                                    enableSorting={false}
                                    enableTopToolbar={false}
                                    enableBottomToolbar={false}
                                    enablePagination={false}
                                    enableColumnResizing={true}
                                    layoutMode='grid'
                                    muiTableContainerProps={{
                                        ref: tableContainerRef,
                                        sx: {
                                            height: tableMaxHeight ? `${tableMaxHeight}px` : undefined,
                                            maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : undefined,
                                            userSelect: 'none',
                                        },
                                        onScroll: handleTableScroll,
                                    }}
                                    muiTableHeadCellProps={{
                                        sx: {
                                            flex: '0 0 auto',
                                        },
                                    }}
                                    muiTableBodyCellProps={{
                                        sx: {
                                            flex: '0 0 auto',
                                            height: '40px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                        },
                                    }}
                                    muiTableBodyRowProps={({ row }) => {
                                        const branch = row.original;

                                        return {
                                            onDoubleClick: async () => {
                                                await FetchData(branch);
                                            },
                                            onClick: () => {
                                                localStorage.setItem('markerID', JSON.stringify(branch.id));
                                                if (selectable) {
                                                    void selectBranch(branch);
                                                }
                                            },
                                            sx: {
                                                backgroundColor: `${row.index % 2 === 0 ? 'white' : '#ececec'}`,
                                                cursor: selectable ? 'pointer' : 'default',
                                            },
                                            hover: false,
                                            selected: selectedBranches.some((selectedBranch) => selectedBranch.id === branch.id),
                                        };
                                    }}
                                />
                            </div>
                        ) : isBranchesLoading ? (
                            <div className='h-100 d-flex align-items-center justify-content-center w-100'>
                                <Spinner animation='border' />
                            </div>
                        ) : (
                            <div className='h-100 d-flex align-items-center w-100'>
                                <NoDataComponent noDataText={t('common.branches')}></NoDataComponent>
                            </div>
                        )}
                    </Suspense>
                </ErrorBoundary>
            </div>
            {branches && selectedBranches.length > 0 && (
                <Suspense
                    fallback={
                        <div className='branch-action-button w-100 d-flex justify-content-center '>
                            <div className='w-100 bg-light border flex-center border-dark mx-2 py-1 me-4 '>
                                <Spinner animation='border'/>
                            </div>
                        </div>
                    }
                >
                    <RequestProvider>
                        <BranchesModal
                            isModalButtonDisabled={
                                activeTab === 'branches' && !!selectedBranches.find((branch) => branch.assignedTask)
                            }
                            selectedBranchId={selectedBranch.id}
                        ></BranchesModal>
                    </RequestProvider>
                </Suspense>
            )}
            {showNewRequestModal && (
                <NewRequest showModal={showNewRequestModal} setShowModal={setShowNewRequestModal}/>
            )}
        </>
    );
};
